import { Box, Button, Checkbox, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { modalStyle } from '@/components/modalStyle';
import { PlanUnit, TrainingPlan } from '@/generated';
import { createWorkoutUnit } from './creationUtils';
import { useUpdatePlan } from '@/hooks/plan-mutation.hooks';
import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

export const UpdateWorkoutUnitModal = (props: { show: boolean; setShow: (show: boolean) => void; plan: TrainingPlan; workoutUnit?: PlanUnit }) => {
  const { plan } = props;
  const lastSortKey = plan.units.reduce((p, c) => (p > c.sortKey ? p : c.sortKey), 0) ?? 0;
  const [editWorkoutUnit, setEditWorkoutUnit] = useState<PlanUnit>(createWorkoutUnit('', lastSortKey + 1));
  const [informViewers, setInformViewers] = useState<boolean>(false);
  const updatePlanMutation = useUpdatePlan();

  useEffect(() => {
    if (props.workoutUnit) setEditWorkoutUnit(props.workoutUnit);
  }, [props.workoutUnit]);

  return (
    <Modal
      open={props.show}
      onClose={() => {
        props.setShow(false);
      }}
    >
      <Box sx={modalStyle}>
        <Typography variant="h4">{props.workoutUnit ? 'Update' : 'Create'} Workout Unit</Typography>
        <Stack sx={{ gap: 1 }}>
          <Stack sx={{ alignItems: 'center', mt: 2 }} direction={'row'}>
            <Checkbox value={informViewers} onChange={(v) => setInformViewers(!informViewers)} />
            <Tooltip title="Send an update email to all users who have this plan assigned.">
              <Typography>Send Update Email</Typography>
            </Tooltip>
          </Stack>
          <DateTimePicker
            ampm={false}
            value={moment(editWorkoutUnit.trainingDateIso)}
            onChange={(v) => {
              if (v) setEditWorkoutUnit({ ...editWorkoutUnit, trainingDateIso: v.toISOString() });
            }}
          />
          <TextField
            sx={{ my: 2 }}
            multiline
            label={'Workout Name'}
            value={editWorkoutUnit.name}
            onChange={(e) => {
              setEditWorkoutUnit({ ...editWorkoutUnit, name: e.currentTarget.value });
            }}
          />
          <Stack alignItems={'center'} direction={'row'} gap={3}>
            <Box display={'flex'} alignItems={'center'}>
              Enable Link
              <Checkbox
                checked={editWorkoutUnit.link !== undefined}
                onChange={(c, v) => {
                  if (!v) setEditWorkoutUnit({ ...editWorkoutUnit, link: undefined });
                  else setEditWorkoutUnit({ ...editWorkoutUnit, link: { title: '', uri: '' } });
                }}
              />
            </Box>
            <Stack flex={1}>
              <TextField
                sx={{ my: 2 }}
                disabled={editWorkoutUnit.link === undefined}
                multiline
                label={'Link Title'}
                value={editWorkoutUnit.link?.title}
                onChange={(e) => {
                  setEditWorkoutUnit({ ...editWorkoutUnit, link: editWorkoutUnit.link ? { ...editWorkoutUnit.link, title: e.currentTarget.value } : undefined });
                }}
              />
              <TextField
                sx={{ my: 2 }}
                multiline
                disabled={editWorkoutUnit.link === undefined}
                label={'Link'}
                value={editWorkoutUnit.link?.uri}
                onChange={(e) => {
                  setEditWorkoutUnit({ ...editWorkoutUnit, link: editWorkoutUnit.link ? { ...editWorkoutUnit.link, uri: e.currentTarget.value } : undefined });
                }}
              />
            </Stack>
          </Stack>
          <TextField
            sx={{}}
            label={'Workout Description'}
            multiline
            minRows={3}
            value={editWorkoutUnit.description}
            onChange={(e) => {
              setEditWorkoutUnit({ ...editWorkoutUnit, description: e.target.value });
            }}
          />
          &nbsp;
        </Stack>
        <Button
          onClick={async () => {
            if (props.workoutUnit) {
              await updatePlanMutation.mutateAsync({
                notifyUsers: informViewers,
                plan: {
                  ...props.plan,
                  units: props.plan.units.map((g) =>
                    g.idClient === props.workoutUnit?.idClient
                      ? { ...g, description: editWorkoutUnit.description, name: editWorkoutUnit.name, trainingDateIso: editWorkoutUnit.trainingDateIso, link: editWorkoutUnit.link }
                      : g,
                  ),
                },
              });
            } else {
              await updatePlanMutation.mutateAsync({
                notifyUsers: informViewers,
                plan: {
                  ...props.plan,
                  units: props.plan.units.concat(editWorkoutUnit),
                },
              });
              setEditWorkoutUnit(createWorkoutUnit('', lastSortKey + 1));
            }

            props.setShow(false);
          }}
        >
          {props.workoutUnit ? 'Update' : 'Create'}
        </Button>
      </Box>
    </Modal>
  );
};
